/**
 * Helper Utilities for Checkout Components
 *
 * This module contains utility functions extracted from the legacy Dispatchers class.
 * These functions contain business logic that was previously embedded in dispatcher methods.
 *
 * Components should use these helpers when needed, then dispatch the appropriate action creators.
 */
import { IAddress } from "../../models/address";
import { DEFAULT_COUNTRY, DEFAULT_PHONE_NUMBER } from "./constants";
import { IReduxFormState } from "./reducers.interfaces";

/**
 * Updates an address field and automatically sets shipping_address_url if the
 * entered address matches a saved address.
 *
 * @param currentState - Current form state
 * @param savedAddresses - List of user's saved addresses
 * @param changedFields - Fields being changed
 * @returns Fields to update including matched address URL
 */
export const updateAddressFieldWithMatching = (
    currentState: IReduxFormState,
    savedAddresses: readonly IAddress[],
    changedFields: Partial<IReduxFormState>,
): Partial<IReduxFormState> => {
    const enteredAddress = {
        shipping_first_name: currentState.shipping_first_name,
        shipping_last_name: currentState.shipping_last_name,
        shipping_line1: currentState.shipping_line1,
        shipping_line2: currentState.shipping_line2,
        shipping_line4: currentState.shipping_line4,
        shipping_state: currentState.shipping_state,
        shipping_postcode: currentState.shipping_postcode,
        shipping_phone_number: currentState.shipping_phone_number,
        shipping_country: currentState.shipping_country,
        ...changedFields,
    };

    const defaultAddr: Partial<IAddress> = {};
    const matchingSavedAddress =
        savedAddresses.filter((addr) => {
            return (
                enteredAddress.shipping_first_name === addr.first_name &&
                enteredAddress.shipping_last_name === addr.last_name &&
                enteredAddress.shipping_line1 === addr.line1 &&
                enteredAddress.shipping_line2 === addr.line2 &&
                enteredAddress.shipping_line4 === addr.line4 &&
                enteredAddress.shipping_state === addr.state &&
                enteredAddress.shipping_postcode === addr.postcode &&
                enteredAddress.shipping_phone_number === addr.phone_number &&
                enteredAddress.shipping_country === addr.country
            );
        })[0] || defaultAddr;

    return {
        ...changedFields,
        shipping_address_url: matchingSavedAddress.url || "",
    };
};

/**
 * Converts a saved address to form field updates for a specific prefix (shipping or billing).
 *
 * @param prefix - Either "shipping" or "billing"
 * @param savedAddresses - List of user's saved addresses
 * @param selectedAddressURL - URL of the selected address
 * @returns Form fields to update
 */
export const selectSavedAddressFields = (
    prefix: "shipping" | "billing",
    savedAddresses: readonly IAddress[],
    selectedAddressURL: string,
): Partial<IReduxFormState> => {
    const defaultAddr: Partial<IAddress> = {};
    const selectedAddress =
        savedAddresses.filter((a) => {
            return a.url === selectedAddressURL;
        })[0] || defaultAddr;

    return {
        [`${prefix}_address_url`]: selectedAddressURL || "",
        [`${prefix}_first_name`]: selectedAddress.first_name || "",
        [`${prefix}_last_name`]: selectedAddress.last_name || "",
        [`${prefix}_line1`]: selectedAddress.line1 || "",
        [`${prefix}_line2`]: selectedAddress.line2 || "",
        [`${prefix}_line4`]: selectedAddress.line4 || "",
        [`${prefix}_state`]: selectedAddress.state || "",
        [`${prefix}_postcode`]: selectedAddress.postcode || "",
        [`${prefix}_phone_number`]:
            selectedAddress.phone_number || DEFAULT_PHONE_NUMBER,
        [`${prefix}_country`]: selectedAddress.country || DEFAULT_COUNTRY,
    };
};
